// Delivery CLIN detail drawer — the read-only, role-masked detail panel for a
// contract line item. Restyled to the delivery-redesign side-panel archetype
// (LAYOUT-SPEC §2C): a header with the CLIN id + title + chips, uppercase group
// labels over hairline-separated field groups, and a pinned footer. Financial
// fields flow through the program's role projection via <MaskedField>, so a
// Simplified/Masked viewer sees "Restricted", never the dollar value.
//
// Data comes from the delivery ContractCLIN (pangaea/ryugu); its rich
// sub-objects arrive JSON-encoded (exercise_info_json, labor_mix_json,
// scoped_pws_refs_json) and are decoded defensively here.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraInlineBanner, SpecraKeyValueRow, SpecraOverlayDrawer, SpecraProgress, SpecraSectionHeader, SpecraTag, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsViewOnly } from '../../../../contexts/authContext';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { formatUtcYmd } from '../../_shared/api/nullable';
import { StageContractLineDrawer } from '../../_shared/components/review/StageContractLineDrawer';
import { SourceChip } from '../../_shared/components/SourceChip';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { formatMoney } from '../../_shared/lib/recordLabels';
import { useProgram } from '../hooks';
import { popHooks } from '../operationalHooks';
import { useProgramModificationChangeSets } from '../v2/modificationHooks';
import { ClinOccurrenceSchedule } from './ClinOccurrenceSchedule';
// Money with cents to match the award document. A $0 with an NSP/TBD flag
// renders as that token; real dollars always win over the sentinel.
function fmtMoney(v, isNsp, isTbd) {
    const amount = v ?? 0;
    if (amount === 0 && isNsp) return 'NSP';
    if (amount === 0 && isTbd) return 'TBD';
    return formatMoney(amount);
}
function parseJsonArray(raw) {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch  {
        return [];
    }
}
function parseJsonObject(raw) {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch  {
        return null;
    }
}
// PWS sections: prefer scoped refs (section + title) when present, else the
// flat pws_sections array.
function pwsSectionStrings(clin) {
    const scoped = parseJsonArray(clin.scopedPwsRefsJson);
    if (scoped.length > 0) {
        return scoped.map((r)=>{
            if (typeof r === 'string') return r;
            const o = r;
            const sec = o.section ?? o.sectionNumber ?? o.pwsSection ?? '';
            const title = o.title ?? o.heading ?? '';
            return title ? `${sec} — ${title}` : String(sec);
        });
    }
    return parseJsonArray(clin.pwsSections).map(String);
}
function attachmentReferenceStrings(clin) {
    return parseJsonArray(clin.attachmentReferences).filter((reference)=>typeof reference === 'string').map((reference)=>reference.trim()).filter(Boolean);
}
function laborMixRows(clin) {
    const obj = parseJsonObject(clin.laborMixJson);
    const arr = obj?.categories ?? obj?.lcats ?? parseJsonArray(clin.laborMixJson);
    return (arr ?? []).map((r)=>{
        const o = r ?? {};
        const category = String(o.category ?? o.lcat ?? o.laborCategory ?? o.name ?? '');
        if (!category) return null;
        const str = (v)=>v === undefined || v === null ? undefined : String(v);
        return {
            category,
            rate: str(o.rate ?? o.hourlyRate),
            hours: str(o.hours ?? o.hoursPerIteration),
            iterationPrice: str(o.iterationPrice ?? o.extendedPrice)
        };
    }).filter((r)=>r !== null);
}
function popProgress(startStr, endStr) {
    if (!startStr || !endStr) return null;
    const start = Date.parse(startStr);
    const end = Date.parse(endStr);
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
    const now = Date.now();
    const pct = Math.max(0, Math.min(1, (now - start) / (end - start)));
    let state;
    let tone;
    if (now < start) {
        state = 'Not started';
        tone = 'brand';
    } else if (now >= end) {
        state = 'Period ended';
        tone = 'muted';
    } else if (pct >= 0.9) {
        state = 'In progress · ending soon';
        tone = 'warning';
    } else {
        state = 'In progress';
        tone = 'success';
    }
    return {
        pct,
        state,
        tone
    };
}
// An optional-task line belongs to its task, not to an option period: its
// window is its own exercise's, never the period its number suggests.
export function optionalTaskNumberOf(clin) {
    const titled = Number(/\boptional\s+task\s*#?\s*(\d+)/i.exec(clin.shortTitle ?? '')?.[1] ?? 0);
    if (clin.isOptionalTask || (clin.optionalTaskNumber ?? 0) > 0) {
        return clin.optionalTaskNumber || titled;
    }
    if ((clin.optionType ?? '').toLowerCase() === 'optional_task') {
        return titled || clin.optionPeriodNumber || 0;
    }
    return titled > 0 ? titled : null;
}
// A CLIN/SLIN often carries no dates of its own; fall back to the program's
// period of performance that matches the line's period (Base, or Option N).
function matchProgramPeriod(clin, periods) {
    if (optionalTaskNumberOf(clin) !== null) return null;
    const isBase = !clin.optionType || clin.optionType.toLowerCase() === 'base';
    if (isBase) {
        return periods.find((p)=>(p.periodType ?? '').toUpperCase() === 'BASE') ?? null;
    }
    const optNum = clin.optionPeriodNumber;
    if (!optNum) return null;
    const options = periods.filter((p)=>(p.periodType ?? '').toUpperCase() === 'OPTION');
    // The option number can live in the `optionNumber` field, but extracted
    // periods often leave it 0 and carry the number only in the name
    // ("Option Period 1"). Try the field, then the name, then fall back to the
    // Nth option period by start date.
    const byField = options.find((p)=>(p.optionNumber ?? 0) === optNum);
    if (byField) return byField;
    const byName = options.find((p)=>{
        const m = (p.periodName ?? '').match(/(\d+)/);
        return m != null && Number(m[1]) === optNum;
    });
    if (byName) return byName;
    const sorted = [
        ...options
    ].sort((a, b)=>(formatUtcYmd(a.startDate) ?? '').localeCompare(formatUtcYmd(b.startDate) ?? ''));
    return sorted[optNum - 1] ?? null;
}
// `numeric` stacks money and counts at body size (G22) instead of the muted caption.
function Field({ label, children, testId, numeric }) {
    return /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
        className: "tabular-nums",
        "data-testid": testId,
        label: label,
        value: children,
        variant: numeric ? 'numeric' : 'default'
    });
}
// The line's price cells exactly as the document prints them, line breaks
// kept, beside the cleaned values above. Prices stay behind the mask; the
// printed quantity ("Up to 500") is a count and shows as is.
function PrintedCells({ clin, money }) {
    const cells = [
        {
            label: 'Unit price',
            text: clin.rawUnitPrice,
            mask: 'unit_price'
        },
        {
            label: 'Quantity',
            text: clin.rawQuantity
        },
        {
            label: 'Extended price',
            text: clin.rawExtendedPrice,
            mask: 'extended_price'
        },
        {
            label: 'Ceiling',
            text: clin.rawCeiling,
            mask: 'nte_ceiling'
        }
    ].filter((c)=>c.text);
    if (cells.length === 0) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-3",
        "data-testid": Testids.deliveryOperational.clinDetailPrintedCells,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "As printed in the document"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-1",
                children: cells.map((c)=>{
                    const text = /*#__PURE__*/ _jsx("span", {
                        className: "whitespace-pre-line",
                        children: c.text
                    });
                    return /*#__PURE__*/ _jsx(Field, {
                        label: c.label,
                        children: c.mask ? money(c.mask, text) : text
                    }, c.label);
                })
            })
        ]
    });
}
// The T&M rate card; rates and iteration prices stay behind the program's mask.
function laborMixColumns(money) {
    return [
        {
            id: 'category',
            header: 'Labor category',
            cell: (r)=>r.category
        },
        {
            id: 'rate',
            header: 'Rate',
            align: 'right',
            cell: (r)=>money('unit_price', /*#__PURE__*/ _jsx("span", {
                    children: r.rate ?? '—'
                }))
        },
        {
            id: 'hours',
            header: 'Hours',
            align: 'right',
            className: 'text-muted-foreground',
            cell: (r)=>r.hours ?? '—'
        },
        {
            id: 'iteration',
            header: 'Iteration $',
            align: 'right',
            cell: (r)=>money('extended_price', /*#__PURE__*/ _jsx("span", {
                    children: r.iterationPrice ?? '—'
                }))
        }
    ];
}
// Thin wrapper: the SpecraOverlayDrawer shell + the CLIN content. Parents null
// the record on close, so the last open line item is snapshotted to keep the
// exit animation showing content instead of an emptied shell.
// A line is corrected as a reviewer item on the program's open intake or
// modification set (SAD-768), never written straight to the line: it carries
// its reason and lineage, and survives a re-extraction.
const CLOSED_SET_STATES = new Set([
    'Applied',
    'Cancelled',
    'Rejected',
    'Superseded'
]);
function openReviewSetId(sets) {
    const open = sets.filter((s)=>(s.changeSetType === 'ContractIntake' || s.changeSetType === 'Modification') && !CLOSED_SET_STATES.has(s.state));
    return (open.find((s)=>s.changeSetType === 'ContractIntake') ?? open[0])?.changeSetId ?? '';
}
// Correct this line: only while the drawer is open, for a person who may edit.
function CorrectLineAction({ clin, programId }) {
    const isViewOnly = useIsViewOnly();
    const tenantId = useTenantId();
    const { data: sets } = useProgramModificationChangeSets(programId, tenantId);
    const reviewSetId = openReviewSetId(sets ?? []);
    const [correcting, setCorrecting] = useState(false);
    if (isViewOnly) return null;
    if (!reviewSetId) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            as: "span",
            size: "meta",
            tone: "muted",
            "data-testid": Testids.deliveryOperational.clinDetailCorrectUnavailable,
            children: "Lines are corrected on an open intake or modification review."
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "secondary",
                onClick: ()=>setCorrecting(true),
                "data-testid": Testids.deliveryOperational.clinDetailCorrect,
                children: "Correct this line"
            }),
            /*#__PURE__*/ _jsx(StageContractLineDrawer, {
                open: correcting,
                onOpenChange: setCorrecting,
                mode: "correct",
                programId: programId,
                changeSetId: reviewSetId,
                initialClinNumber: clin.clinNumber ?? '',
                initialTitle: clin.shortTitle ?? ''
            })
        ]
    });
}
export function ClinDetailDrawer({ clin, programId, isOpen, onClose }) {
    const navigate = useNavigate();
    // Backing zou project — lets SourceChips open the source PDF at the ref's page.
    const { data: chipProg } = useProgram(programId);
    const chipProjectId = chipProg?.program?.zouProjectId ?? '';
    const last = useRef(clin);
    if (clin) last.current = clin;
    const shown = clin ?? last.current;
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        headerActions: isOpen && shown ? /*#__PURE__*/ _jsx(CorrectLineAction, {
            clin: shown,
            programId: programId
        }) : undefined,
        open: isOpen,
        onOpenChange: (next)=>{
            if (!next) onClose();
        },
        eyebrow: shown ? /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex items-center gap-[var(--space-1-5)]",
            children: [
                /*#__PURE__*/ _jsxs("span", {
                    className: "font-mono tracking-[0.02em] tabular-nums",
                    children: [
                        "CLIN ",
                        shown.clinNumber
                    ]
                }),
                /*#__PURE__*/ _jsx(SourceChip, {
                    projectId: chipProjectId,
                    targetObjectType: "CLIN",
                    targetObjectId: `${programId}/${shown.clinNumber ?? ''}`,
                    fieldPath: "clin_number"
                })
            ]
        }) : undefined,
        title: shown?.shortTitle || shown?.description || 'Contract Line Item',
        testId: Testids.deliveryOperational.clinDetailDrawer,
        footerProps: {
            confirmLabel: 'View deliverables',
            onConfirm: ()=>navigate(`/delivery/programs/${programId}/deliverables`)
        },
        children: shown ? /*#__PURE__*/ _jsx(ClinDrawerContent, {
            clin: shown,
            programId: programId
        }) : null
    });
}
export function ClinDrawerContent({ clin, programId }) {
    // Backing zou project — lets SourceChips open the source PDF at the ref's page.
    const { data: chipProg } = useProgram(programId);
    const chipProjectId = chipProg?.program?.zouProjectId ?? '';
    // Program periods — the fallback source when a line item has no PoP of its own.
    const { data: periodData } = popHooks.useList({
        programId
    });
    // Provenance keyed on the CLIN's program-scoped object id (same convention as
    // the CLINs table) so each field's SourceChip resolves its source ref.
    const clinSourceId = `${programId}/${clin.clinNumber ?? ''}`;
    // Financial fields are gated on the PROGRAM's projection; fail-closed while loading.
    const programRef = {
        kind: 'program',
        id: programId,
        objectType: 'delivery.programs',
        objectId: programId
    };
    const money = (fieldName, node)=>/*#__PURE__*/ _jsx(MaskedField, {
            fieldName: fieldName,
            resource: programRef,
            children: node
        });
    const nsp = !!clin.pricingIsNsp;
    const task = optionalTaskNumberOf(clin);
    const isBase = task === null && (!clin.optionType || clin.optionType.toLowerCase() === 'base');
    const periodLabel = task !== null ? task > 0 ? `Optional task ${task}` : 'Optional task' : isBase ? 'Base' : clin.optionPeriodNumber ? `Option ${clin.optionPeriodNumber}` : 'Option';
    const pwsRefs = pwsSectionStrings(clin);
    const attachmentRefs = attachmentReferenceStrings(clin);
    const rates = laborMixRows(clin);
    const exercise = parseJsonObject(clin.exerciseInfoJson);
    // Prefer the line item's own PoP; otherwise derive it from the matching
    // program period and flag it as derived so the source is never implied to be
    // the CLIN itself.
    // The line item's own dates are plain "YYYY-MM-DD" strings; program-period
    // dates are Timestamp protos, so normalize the derived source through
    // formatUtcYmd. Both branches yield a plain date string (or undefined).
    const hasOwnPop = !!clin.popStartDate && !!clin.popEndDate;
    const derivedPeriod = hasOwnPop ? null : matchProgramPeriod(clin, periodData?.items ?? []);
    // Use || not ?? — a line item's own popStartDate is often an empty string
    // (not undefined), which ?? would NOT fall through, leaving no date/bar.
    const popStart = clin.popStartDate || formatUtcYmd(derivedPeriod?.startDate);
    const popEnd = clin.popEndDate || formatUtcYmd(derivedPeriod?.endDate);
    const popDerived = !hasOwnPop && !!popStart && !!popEnd;
    const pop = popProgress(popStart, popEnd);
    const popRange = popStart && popEnd ? `${popStart} – ${popEnd}` : '';
    const hasPop = Boolean(popRange) || Boolean(clin.popPeriodName) || (clin.popDurationMonths ?? 0) > 0;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-[var(--space-card)] flex flex-wrap gap-[var(--space-1-5)]",
                children: [
                    clin.contractType && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: clin.contractType
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: isBase ? 'neutral' : 'info',
                        children: isBase ? 'Base Period' : periodLabel
                    }),
                    clin.quantity ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "neutral",
                        children: [
                            clin.quantity,
                            clin.unit ? ` ${clin.unit}` : ''
                        ]
                    }) : null,
                    clin.isSublineItem && clin.parentClinNumber && /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "neutral",
                        children: [
                            "Sub of ",
                            clin.parentClinNumber
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Pricing"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2",
                children: [
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Unit price",
                        numeric: true,
                        children: money('unit_price', /*#__PURE__*/ _jsx("span", {
                            children: fmtMoney(clin.pricingUnitPrice, clin.pricingIsNsp, clin.pricingIsTbd)
                        }))
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Quantity",
                        numeric: true,
                        children: clin.quantity ? `${clin.quantity}${clin.unit ? ` ${clin.unit}` : ''}` : '—'
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Extended price",
                        numeric: true,
                        children: money('extended_price', /*#__PURE__*/ _jsx("span", {
                            children: fmtMoney(clin.pricingExtendedPrice, clin.pricingIsNsp, clin.pricingIsTbd)
                        }))
                    }),
                    clin.pricingIsNte && /*#__PURE__*/ _jsx(Field, {
                        label: "NTE ceiling",
                        numeric: true,
                        testId: Testids.deliveryOperational.clinDetailNteCeiling,
                        children: money('nte_ceiling', /*#__PURE__*/ _jsx("span", {
                            children: (clin.pricingNteCeiling ?? 0) === 0 ? '—' : fmtMoney(clin.pricingNteCeiling)
                        }))
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Award amount",
                        numeric: true,
                        children: money('award_amount', /*#__PURE__*/ _jsxs("span", {
                            className: "inline-flex items-center",
                            children: [
                                fmtMoney(clin.pricingAwardAmount, clin.pricingIsNsp, clin.pricingIsTbd),
                                /*#__PURE__*/ _jsx(SourceChip, {
                                    projectId: chipProjectId,
                                    targetObjectType: "CLIN",
                                    targetObjectId: clinSourceId,
                                    fieldPath: "awarded_amount",
                                    resource: programRef,
                                    maskFieldName: "award_amount"
                                })
                            ]
                        }))
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Funded amount",
                        numeric: true,
                        children: money('funded_amount', /*#__PURE__*/ _jsxs("span", {
                            className: "inline-flex items-center",
                            children: [
                                (clin.pricingFundedAmount ?? 0) === 0 && !clin.pricingIsNsp && !clin.pricingIsTbd && !clin.fundingStatus ? '—' : fmtMoney(clin.pricingFundedAmount),
                                /*#__PURE__*/ _jsx(SourceChip, {
                                    projectId: chipProjectId,
                                    targetObjectType: "CLIN",
                                    targetObjectId: clinSourceId,
                                    fieldPath: "funded_amount",
                                    resource: programRef,
                                    maskFieldName: "funded_amount"
                                })
                            ]
                        }))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PrintedCells, {
                clin: clin,
                money: money
            }),
            nsp && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                className: "mt-3",
                variant: "framed",
                children: [
                    "Not Separately Priced —",
                    ' ',
                    clin.isSublineItem && clin.parentClinNumber ? `the cost of this sub-item is carried on CLIN ${clin.parentClinNumber}.` : 'award and funded amounts land with the exercise modification. This line carries no independent dollar value yet.'
                ]
            }),
            hasPop && /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Period of Performance",
                        action: popDerived && /*#__PURE__*/ _jsx(SpecraBadge, {
                            title: "No period of performance on this line item — derived from the matching program period.",
                            variant: "neutral",
                            children: "Derived"
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2 flex items-baseline gap-2",
                        children: [
                            popRange && /*#__PURE__*/ _jsx("span", {
                                className: "text-[length:var(--fs-ui)] tabular-nums text-foreground",
                                children: popRange
                            }),
                            clin.popDurationMonths ? /*#__PURE__*/ _jsxs("span", {
                                className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                children: [
                                    clin.popDurationMonths,
                                    " months"
                                ]
                            }) : clin.popPeriodName ? /*#__PURE__*/ _jsx("span", {
                                className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                children: clin.popPeriodName
                            }) : null
                        ]
                    }),
                    pop && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraProgress, {
                                "aria-label": `Period of performance elapsed: ${pop.state}`,
                                className: "mt-2",
                                size: "thin",
                                tone: pop.tone,
                                value: Math.round(pop.pct * 100)
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                className: "mt-1",
                                size: "meta",
                                tone: "muted",
                                children: pop.state
                            })
                        ]
                    })
                ]
            }),
            (pwsRefs.length > 0 || clin.description) && /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Scope",
                        action: pwsRefs.length > 0 && /*#__PURE__*/ _jsxs("ul", {
                            "data-testid": Testids.deliveryOperational.clinDetailPwsRefs,
                            className: "flex flex-wrap items-center gap-[5px]",
                            children: [
                                pwsRefs.map((s)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "neutral",
                                            className: "font-mono",
                                            children: s
                                        })
                                    }, s)),
                                /*#__PURE__*/ _jsx(SourceChip, {
                                    projectId: chipProjectId,
                                    targetObjectType: "CLIN",
                                    targetObjectId: clinSourceId,
                                    fieldPath: "pws_section_refs"
                                })
                            ]
                        })
                    }),
                    clin.description && /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 whitespace-pre-wrap text-[length:var(--fs-ui-body)] leading-[1.65] text-muted-foreground",
                        children: clin.description
                    })
                ]
            }),
            attachmentRefs.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "mt-[var(--space-5-5)]",
                children: /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                    variant: "group",
                    title: "Attachments",
                    action: /*#__PURE__*/ _jsx("ul", {
                        className: "flex flex-wrap items-center gap-[var(--space-1-5)]",
                        children: attachmentRefs.map((reference, index)=>/*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(SpecraTag, {
                                    children: reference
                                })
                            }, `${reference}-${index}`))
                    })
                })
            }),
            exercise && Object.keys(exercise).length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Exercise & Performance"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2",
                        children: [
                            exercise.farClause ? /*#__PURE__*/ _jsx(Field, {
                                label: "FAR clause",
                                children: String(exercise.farClause)
                            }) : null,
                            exercise.exerciseFrequency ? /*#__PURE__*/ _jsx(Field, {
                                label: "Exercise frequency",
                                children: String(exercise.exerciseFrequency)
                            }) : null,
                            exercise.popMonths ? /*#__PURE__*/ _jsx(Field, {
                                label: "PoP (months)",
                                children: String(exercise.popMonths)
                            }) : null,
                            exercise.popDescription ? /*#__PURE__*/ _jsx(Field, {
                                label: "PoP detail",
                                children: String(exercise.popDescription)
                            }) : null
                        ]
                    })
                ]
            }),
            clin.hasLaborMix && rates.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Labor Categories (T&M Rate Card)"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-2",
                        "data-testid": Testids.deliveryOperational.clinDetailLaborMix,
                        children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                            columns: laborMixColumns(money),
                            framed: true,
                            getRowId: (r)=>r.key,
                            rows: rates.map((r, i)=>({
                                    ...r,
                                    key: `${r.category}-${i}`
                                }))
                        })
                    })
                ]
            }),
            clin.id ? /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                "data-testid": Testids.deliveryOperational.clinDetailDeliverables,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Deliverables"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-2",
                        children: /*#__PURE__*/ _jsx(ClinOccurrenceSchedule, {
                            programId: programId,
                            clinId: clin.id
                        })
                    })
                ]
            }) : null
        ]
    });
}
